/**
 * Minimal ABIs, hand-written rather than imported wholesale from the artifacts.
 *
 * The portal only needs a handful of functions, and a trimmed ABI means a
 * mistyped function name is a compile error instead of a runtime revert.
 */
import { parseAbi } from "viem";

export const erc20Abi = parseAbi([
  "function name() view returns (string)",
  "function symbol() view returns (string)",
  "function decimals() view returns (uint8)",
  "function totalSupply() view returns (uint256)",
  "function balanceOf(address owner) view returns (uint256)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function transfer(address to, uint256 amount) returns (bool)",
]);

export const swapAbi = parseAbi([
  "event Swapped(address indexed account, uint256 amount)",
  "function LEGACY_TOKEN() view returns (address)",
  "function ASETQU() view returns (address)",
  "function SWAP_START() view returns (uint256)",
  "function SWAP_END() view returns (uint256)",
  "function WALLET_CAP() view returns (uint256)",
  "function BURN_ADDRESS() view returns (address)",
  "function totalSwapped() view returns (uint256)",
  "function swappedBy(address account) view returns (uint256)",
  "function isDenied(address account) view returns (bool)",
  "function remainingCapOf(address account) view returns (uint256)",
  "function remaining() view returns (uint256)",
  "function paused() view returns (bool)",
  "function swap(uint256 amount)",
]);

export const stakingAbi = parseAbi([
  "struct Plan { uint64 duration; uint16 rewardRateBps; uint128 minStake; bool earlyExitAllowed; bool active; }",
  "struct Position { address owner; uint32 planId; uint64 startTime; uint128 principal; uint128 reward; uint64 unlockTime; bool earlyExitAllowed; bool withdrawn; }",
  "function ASETQU() view returns (address)",
  "function paused() view returns (bool)",
  "function totalPrincipal() view returns (uint256)",
  "function reservedRewards() view returns (uint256)",
  "function availableRewards() view returns (uint256)",
  "function isSolvent() view returns (bool)",
  "function planCount() view returns (uint256)",
  "function getPlan(uint32 planId) view returns (Plan)",
  "function getPosition(uint256 positionId) view returns (Position)",
  "function positionsOf(address account) view returns (uint256[])",
  "function previewStake(uint32 planId, uint256 amount) view returns (uint256 reward, bool fundable)",
  "function isMature(uint256 positionId) view returns (bool)",
  "function stake(uint32 planId, uint256 amount) returns (uint256)",
  "function unstake(uint256 positionId)",
  "function unstakeEarly(uint256 positionId)",
]);

export const permanentLockVaultAbi = parseAbi([
  "function ASETQU() view returns (address)",
  "function lockedThroughVault() view returns (uint256)",
  "function lockedBy(address account) view returns (uint256)",
  "function totalPermanentlyLocked() view returns (uint256)",
  "function effectiveCirculatingSupply() view returns (uint256)",
  "function permanentUtilityVault(uint256 amount)",
]);

/**
 * `AsetquUtilityRegistry` — the read surface the verification portal needs.
 *
 * `effectiveStatus` is listed before `getRecord` on purpose: it is the one
 * callers should reach for. `getRecord().status` is the raw stored field and
 * does not account for a lapsed `expiresAt`.
 */
export const utilityRegistryAbi = parseAbi([
  "struct RegistryRecord { bytes32 parentId; address operator; address module; address permanentVault; uint32 version; uint8 registryType; uint8 utilityType; uint8 status; uint256 certificateTokenId; uint64 registeredAt; uint64 activatedAt; uint64 expiresAt; bytes4 interfaceId; bytes32 metadataHash; }",
  "function ASETQU_TOKEN() view returns (address)",
  "function ACTIVATION_DELAY() view returns (uint64)",
  "function VAULT_FACTORY() view returns (address)",
  "function certificateContract() view returns (address)",
  "function effectiveStatus(bytes32 registryId, uint32 version) view returns (uint8)",
  "function isRecordUsable(bytes32 registryId) view returns (bool)",
  "function isActiveModule(bytes32 registryId, address module) view returns (bool)",
  "function isKnownModuleAddress(address module) view returns (bool)",
  "function getRecord(bytes32 registryId, uint32 version) view returns (RegistryRecord)",
  "function getActiveRecord(bytes32 registryId) view returns (address module, address permanentVault, uint32 version, uint8 utilityType)",
  "function vaultRegistryId(address vault) view returns (bytes32)",
  "function certificateRecord(uint256 tokenId) view returns (bytes32)",
  "function recordIdCount() view returns (uint256)",
  "function versionCount(bytes32 registryId) view returns (uint256)",
  "function childCount(bytes32 vendorId) view returns (uint256)",
  "function listRecordIds(uint256 offset, uint256 limit) view returns (bytes32[])",
  "function listVersions(bytes32 registryId, uint256 offset, uint256 limit) view returns (uint32[])",
  "function listChildren(bytes32 vendorId, uint256 offset, uint256 limit) view returns (bytes32[])",
]);

/*
There was a governance write surface here, and it is gone with the console.

`utilityRegistryAdminAbi` and `utilityCertificateAdminAbi` described
`registerRecord`, `approveVendor`, `issue`, `setCertificate` and
`setCertificateContract` so the rehearsal pages could build calldata for them.
Every one of those needs a role that belongs to the Safe, so the browser could
only ever *propose* — and now it does not do that either: the server files one
`onboardVendor` per confirmed application and the owners sign it in the Safe
app. Governance calldata that still needs building by hand comes from
`scripts/registry/govern.ts`, which is where the mainnet path always was.

What is left below is the read surface, which is all a public verification page
was ever entitled to.
*/

export const permanentUtilityVaultAbi = parseAbi([
  "function asetquToken() view returns (address)",
  "function utilityId() view returns (bytes32)",
  "function registry() view returns (address)",
  "function lockedBalance() view returns (uint256)",
  "function depositedTotal() view returns (uint256)",
  "function depositedFor(address account) view returns (uint256)",
]);

/** The credential NFT. Status is deliberately absent — read it from the
 *  registry, never from the token. */
export const utilityCertificateAbi = parseAbi([
  "function ownerOf(uint256 tokenId) view returns (address)",
  "function registryIdOf(uint256 tokenId) view returns (bytes32)",
  "function versionOf(uint256 tokenId) view returns (uint32)",
  "function issuedAt(uint256 tokenId) view returns (uint64)",
  "function locked(uint256 tokenId) view returns (bool)",
  "function tokenURI(uint256 tokenId) view returns (string)",
]);
